import { formatCurrency } from './crmPageUtils';

export const VISITOR_BUSINESS_CONTEXTS = Object.freeze({
  FOR_RENT: 'for_rent',
  FOR_SALE: 'for_sale',
});

const RENT_ALIASES = new Set([
  'for_rent',
  'rent',
  'rental',
  'aluguel',
  'locacao',
  'locação',
]);

const SALE_ALIASES = new Set([
  'for_sale',
  'sale',
  'purchase',
  'venda',
  'compra',
]);

const CASH_PAYMENT_ALIASES = new Set(['cash', 'a_vista', 'à_vista']);
const FINANCED_PAYMENT_ALIASES = new Set(['financed', 'financiamento']);

const firstValue = value => (Array.isArray(value) ? value[0] : value);

export const normalizeVisitorBusinessContext = value => {
  const raw = String(firstValue(value) || '')
    .trim()
    .toLowerCase();

  if (SALE_ALIASES.has(raw)) return VISITOR_BUSINESS_CONTEXTS.FOR_SALE;
  if (RENT_ALIASES.has(raw)) return VISITOR_BUSINESS_CONTEXTS.FOR_RENT;
  return VISITOR_BUSINESS_CONTEXTS.FOR_RENT;
};

export const isRentVisitorContext = value =>
  normalizeVisitorBusinessContext(value) === VISITOR_BUSINESS_CONTEXTS.FOR_RENT;

export const isSaleVisitorContext = value =>
  normalizeVisitorBusinessContext(value) === VISITOR_BUSINESS_CONTEXTS.FOR_SALE;

export const shouldRenderOfferSection = businessContext =>
  isRentVisitorContext(businessContext);

export const normalizePaymentType = value => {
  const raw = String(value || '')
    .trim()
    .toLowerCase();

  if (CASH_PAYMENT_ALIASES.has(raw) || raw.includes('vista')) return 'cash';
  if (FINANCED_PAYMENT_ALIASES.has(raw) || raw.includes('financi')) {
    return 'financed';
  }
  if (raw.includes('fgts')) return 'financed';
  return raw;
};

export const shouldRenderPaymentTypeSection = (businessContext, paymentType) =>
  shouldRenderOfferSection(businessContext) && Boolean(paymentType);

export const shouldRenderSuggestedHouses = (businessContext, houses = []) =>
  isRentVisitorContext(businessContext) && houses.length > 0;

export const numericValue = value => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const lowerLimitValue = visitorInfo =>
  numericValue(
    visitorInfo?.lowerLimitHouseValue ??
      visitorInfo?.lower_limit_house_value ??
      visitorInfo?.budget_min
  );

export const upperLimitValue = visitorInfo =>
  numericValue(
    visitorInfo?.upperLimitHouseValue ??
      visitorInfo?.upper_limit_house_value ??
      visitorInfo?.budget_max
  );

export const shouldRenderPriceRange = visitorInfo =>
  lowerLimitValue(visitorInfo) !== null;

export const priceRangeLabel = visitorInfo => {
  const lower = lowerLimitValue(visitorInfo);
  if (lower === null) return '';

  const upper = upperLimitValue(visitorInfo);
  if (upper === null) return formatCurrency(lower);

  return `${formatCurrency(lower)} a ${formatCurrency(upper)}`;
};

export const visitsQuantity = visitorInfo =>
  numericValue(visitorInfo?.visitsQuantity ?? visitorInfo?.visits_quantity) ||
  0;

export const proposalsInProgressQuantity = visitorInfo =>
  numericValue(
    visitorInfo?.proposalsInProgressQuantity ??
      visitorInfo?.proposals_in_progress_quantity
  ) || 0;

export const proposalsQuantity = visitorInfo =>
  numericValue(
    visitorInfo?.proposalsQuantity ?? visitorInfo?.proposals_quantity
  ) || 0;

export const visitorPaymentType = visitorInfo =>
  visitorInfo?.paymentType || visitorInfo?.payment_type || '';

export const visitorName = visitorInfo =>
  visitorInfo?.visitorName || visitorInfo?.visitor_name || '';

export const visitorFirstName = visitorInfo =>
  visitorInfo?.visitorFirstName ||
  visitorInfo?.visitor_first_name ||
  visitorName(visitorInfo).split(/\s+/).filter(Boolean)[0] ||
  '';

export const visitorPhone = visitorInfo =>
  visitorInfo?.visitorPhone || visitorInfo?.visitor_phone || '';

export const visitorClientSince = visitorInfo =>
  visitorInfo?.clientSince ||
  visitorInfo?.client_since ||
  visitorInfo?.createdAt ||
  visitorInfo?.created_at ||
  '';

export const visitorClientSinceLabel = (visitorInfo, locale = 'pt-BR') => {
  const value = visitorClientSince(visitorInfo);
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

export const visitorAttendanceStage = visitorInfo =>
  visitorInfo?.attendanceStage || visitorInfo?.attendance_stage || '';

export const visitorAttendanceStageLabel = visitorInfo =>
  visitorInfo?.attendanceStageLabel ||
  visitorInfo?.attendance_stage_label ||
  '';

export const isPfaVisitor = (visitorInfo, lead = {}) => {
  const attrs =
    lead?.additional_attributes ||
    lead?.additionalAttributes ||
    visitorInfo?.additional_attributes ||
    visitorInfo?.additionalAttributes ||
    {};
  const value =
    visitorInfo?.clientType ||
    visitorInfo?.client_type ||
    lead?.client_type ||
    lead?.clientType ||
    attrs.client_type ||
    attrs.clientType ||
    attrs.pfa;

  return ['PFA', 'pfa', true, 'true'].includes(value);
};

const normalizeTags = value => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(tag => tag.trim())
      .filter(Boolean);
  }
  return [];
};

export const visitorProfileTags = (visitorInfo, lead = {}) => {
  const attrs =
    lead?.additional_attributes ||
    lead?.additionalAttributes ||
    visitorInfo?.additional_attributes ||
    visitorInfo?.additionalAttributes ||
    {};
  const tags = [
    ...normalizeTags(visitorInfo?.profileTags || visitorInfo?.profile_tags),
    ...normalizeTags(visitorInfo?.labels || visitorInfo?.label_list),
    ...normalizeTags(lead?.profileTags || lead?.profile_tags),
    ...normalizeTags(lead?.labels || lead?.label_list),
    ...normalizeTags(attrs.profileTags || attrs.profile_tags),
  ];

  return [...new Set(tags.map(tag => String(tag).trim()).filter(Boolean))];
};

export const isFirstVisit = visitorInfo =>
  Boolean(visitorInfo?.isFirstVisit ?? visitorInfo?.is_first_visit);

export const isFirstPurchase = visitorInfo =>
  Boolean(
    visitorInfo?.isFirstPurchase ??
      visitorInfo?.is_first_purchase ??
      visitorInfo?.firstPurchase ??
      visitorInfo?.first_purchase ??
      visitorInfo?.primeira_compra
  );

export const isInvestor = visitorInfo =>
  Boolean(visitorInfo?.isInvestor ?? visitorInfo?.is_investor);

export const houseId = house => house?.houseId || house?.house_id || house?.id;

export const houseAddressLine = house =>
  [
    house?.address,
    house?.number,
    house?.neighbourhood || house?.neighborhood,
    house?.city,
  ]
    .filter(Boolean)
    .join(', ');

export const houseRegionLine = house =>
  [house?.city, house?.neighbourhood || house?.neighborhood]
    .filter(Boolean)
    .join(', ');

export const houseDetailsLine = house =>
  [
    house?.area || house?.total_area
      ? `${house.area || house.total_area} m²`
      : '',
    house?.bedrooms || house?.number_of_bedrooms
      ? `${house.bedrooms || house.number_of_bedrooms} quarto(s)`
      : '',
    house?.parking_spots || house?.number_of_parking_spaces
      ? `${house.parking_spots || house.number_of_parking_spaces} vaga(s)`
      : '',
  ]
    .filter(Boolean)
    .join(' • ');

export const propertyMatchForHouse = house =>
  house?.property_match ||
  house?.propertyMatch ||
  house?.match ||
  house?.property_match_snapshot ||
  null;

export const propertyMatchBreakdown = value => {
  const match = value?.breakdown ? value : propertyMatchForHouse(value);
  return (
    match?.breakdown || match?.score_breakdown || match?.scoreBreakdown || {}
  );
};

export const propertyMatchHasBreakdown = value => {
  const match = value?.breakdown ? value : propertyMatchForHouse(value);
  return Boolean(match && Object.keys(propertyMatchBreakdown(match)).length);
};

export const propertyMatchScoreLabel = match => {
  const value =
    match?.score ??
    match?.breakdown?.decision_score ??
    match?.breakdown?.decisionScore ??
    null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';

  return `${Math.round(numeric)} / 100`;
};

export const propertyMatchStatusLabel = match => {
  const status = match?.status || match?.breakdown?.decision_status || '';
  return (
    {
      eligible: 'Compatível',
      suppressed: 'Suprimido',
      rejected: 'Rejeitado',
    }[status] || status
  );
};

export const propertyMatchReasonLabels = match => {
  const breakdown = propertyMatchBreakdown(match);
  const reasons =
    match?.positive_reasons ||
    match?.positiveReasons ||
    breakdown.positive_reasons ||
    breakdown.positiveReasons ||
    [];

  return reasons
    .map(reason => reason?.label || reason?.code || reason)
    .filter(Boolean);
};

const formatDistanceLabel = meters => {
  const numeric = Number(meters);
  if (!Number.isFinite(numeric)) return '';
  if (numeric >= 1000) return `${(numeric / 1000).toFixed(1)} km`;

  return `${Math.round(numeric)} m`;
};

export const propertyMatchGeoRows = match => {
  const geography = propertyMatchBreakdown(match).geography || {};
  const rows = [];
  const text = geography.text || {};
  if (text.criteria_present) {
    rows.push({
      key: 'text',
      label: 'Região',
      value: text.matched
        ? (text.expected_regions || []).join(', ')
        : 'Fora da região persistida',
    });
  }

  const distance = geography.distance || {};
  if (distance.meters !== undefined || distance.radius_meters !== undefined) {
    rows.push({
      key: 'distance',
      label: 'Distância',
      value: [
        formatDistanceLabel(distance.meters),
        formatDistanceLabel(distance.radius_meters),
      ]
        .filter(Boolean)
        .join(' de '),
    });
  }

  const matchedPolygons = Array.isArray(geography.polygons)
    ? geography.polygons.filter(polygon => polygon?.matched)
    : [];
  if (matchedPolygons.length) {
    rows.push({
      key: 'polygon',
      label: 'Polígono',
      value: matchedPolygons
        .map(polygon => polygon.id)
        .filter(Boolean)
        .join(', '),
    });
  }

  return rows.filter(row => row.value);
};

export const visitHouse = visit => visit?.house || visit?.imovel || {};

export const visitCode = visit =>
  visit?.visitCode || visit?.visit_code || visit?.id || visit?.scheduling_id;

export const visitScheduledAt = visit =>
  visit?.scheduledAt || visit?.scheduled_at || '';

export const visitDateTimeLabel = (visit, locale = 'pt-BR') => {
  const scheduledAt = visitScheduledAt(visit);
  if (scheduledAt) {
    const date = new Date(scheduledAt);
    if (!Number.isNaN(date.getTime())) {
      return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    }
  }

  return [visit?.day, visit?.time].filter(Boolean).join(' ');
};

export const isAgentVisit = visit =>
  Boolean(visit?.isAgentVisit ?? visit?.is_agent_visit);

export const lastVisitPrice = (visit, businessContext) => {
  const house = visitHouse(visit);
  return isSaleVisitorContext(businessContext)
    ? house?.salePrice || house?.sale_price
    : house?.rent || house?.rent_price;
};

export const listingUrl = (accountId, id) =>
  accountId && id ? `/app/accounts/${accountId}/imoveis/${id}` : '';

export const visitDetailsUrl = (accountId, id) =>
  accountId && id ? `/app/accounts/${accountId}/visitas/${id}` : '';

export const offerDeepLinkUrl = ({ accountId, visitorId, houseId: id }) => {
  if (!accountId || !visitorId || !id) return '';

  const params = new URLSearchParams({
    contact_id: visitorId,
    imovel_id: id,
  });
  return `/app/accounts/${accountId}/propostas?${params.toString()}`;
};

export const visitorOfferId = offer =>
  offer?.id || offer?.offer_id || offer?.property_offer_id;

export const visitorOfferHouse = offer =>
  offer?.house || offer?.imovel || offer?.property || {};

export const visitorOfferReference = offer =>
  offer?.booking_reference ||
  offer?.bookingReference ||
  offer?.sales_flow_id ||
  offer?.salesFlowId ||
  visitorOfferId(offer);

export const visitorOfferAmountLabel = offer =>
  formatCurrency(offer?.offer_amount || offer?.offerAmount);

export const visitorOfferStatusLabel = offer =>
  offer?.macro_status_label ||
  offer?.macroStatusLabel ||
  offer?.status_label ||
  offer?.statusLabel ||
  {
    submitted: 'Proposta recebida',
    accepted: 'Proposta aceita',
    declined: 'Proposta recusada',
    withdrawn: 'Proposta retirada',
  }[offer?.status] ||
  'Proposta em andamento';

export const visitorOfferPaymentLabel = offer =>
  offer?.payment_label ||
  offer?.paymentLabel ||
  {
    a_vista: 'À vista',
    financiamento: 'Financiado',
    a_vista_fgts: 'À vista + FGTS',
    financiamento_fgts: 'Financiado + FGTS',
  }[offer?.payment_kind || offer?.paymentKind] ||
  '';

export const visitorOfferSubmittedAtLabel = (offer, locale = 'pt-BR') => {
  const value = offer?.submitted_at || offer?.submittedAt || offer?.created_at;
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

export const visitorOfferDetailUrl = ({
  accountId,
  offer,
  visitorId,
  statusBucket = 'in_negotiation',
}) => {
  const id = visitorOfferId(offer);
  if (!accountId || !id) return '';

  const params = new URLSearchParams({
    offer_id: String(id),
    status_bucket: statusBucket,
  });
  if (visitorId) params.set('contact_id', String(visitorId));

  return `/app/accounts/${accountId}/propostas?${params.toString()}`;
};

export const activeBusinessContextFromLead = lead => {
  const attrs = lead?.additional_attributes || lead?.additionalAttributes || {};
  return normalizeVisitorBusinessContext(
    lead?.business_context ||
      lead?.businessContext ||
      attrs.business_context ||
      attrs.businessContext ||
      attrs.operacao ||
      attrs.operation
  );
};
